/**
 * GRIDHEIMR — Bot Logs Component
 *
 * Displays paginated log entries for a bot instance with filtering and search.
 */

import React, { useEffect, useRef, useState } from "react";
import { useLogs } from "../hooks/useLogs";
import { LogLevel } from "../api/types";
import { BLUE, YELLOW, DARK_BG, PIXEL, MONO } from "../theme";

/**
 * Component props
 */
interface BotLogsProps {
  /**
   * Bot ID to fetch logs for
   */
  botId: string;
}

/**
 * Format timestamp to readable string
 */
function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);

  // Show relative time for recent logs
  if (diffSecs < 60) return `${diffSecs}s ago`;
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;

  // Show full timestamp for older logs
  return date.toLocaleString();
}

/**
 * Get log level styling
 */
function getLogLevelStyle(level: LogLevel): React.CSSProperties {
  const baseStyle: React.CSSProperties = {
    padding: "0.25rem 0.5rem",
    fontFamily: PIXEL,
    fontSize: "0.35rem",
    fontWeight: 400,
    textTransform: "uppercase",
    display: "inline-block",
    minWidth: "70px",
    textAlign: "center",
    letterSpacing: "0.08em",
    border: "1px solid",
  };

  switch (level) {
    case LogLevel.DEBUG:
      return {
        ...baseStyle,
        background: "rgba(255,255,255,0.03)",
        color: "rgba(255,255,255,0.4)",
        borderColor: "rgba(255,255,255,0.1)",
      };
    case LogLevel.INFO:
      return {
        ...baseStyle,
        background: `rgba(0,106,167,0.1)`,
        color: BLUE,
        borderColor: `rgba(0,106,167,0.3)`,
      };
    case LogLevel.WARNING:
      return {
        ...baseStyle,
        background: `rgba(254,204,0,0.08)`,
        color: YELLOW,
        borderColor: `rgba(254,204,0,0.25)`,
      };
    case LogLevel.ERROR:
      return {
        ...baseStyle,
        background: "rgba(239,68,68,0.1)",
        color: "#fca5a5",
        borderColor: "rgba(239,68,68,0.3)",
      };
    case LogLevel.CRITICAL:
      return {
        ...baseStyle,
        background: "rgba(220,38,38,0.15)",
        color: "#fecaca",
        borderColor: "rgba(220,38,38,0.4)",
      };
    default:
      return baseStyle;
  }
}

/**
 * Bot Logs Component
 *
 * Fetches and displays real-time logs for a bot instance with filtering and search.
 */
export function BotLogs({ botId }: BotLogsProps) {
  const [selectedLevel, setSelectedLevel] = useState<LogLevel | undefined>(
    undefined
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [autoScroll, setAutoScroll] = useState(true);
  const logsEndRef = useRef<HTMLDivElement>(null);

  const { logs, loading, error } = useLogs(botId, {
    lines: 200,
    enablePolling: true,
  });

  /**
   * Handle scroll on the logs container — pause auto-scroll when user
   * scrolls up, resume when they scroll back to the bottom.
   */
  const handleLogsScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    setAutoScroll(atBottom);
  };

  /**
   * Auto-scroll to bottom when new logs arrive (only if user is at bottom)
   */
  useEffect(() => {
    if (autoScroll && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [logs, autoScroll]);

  /**
   * Filter logs by search query
   */
  const filteredLogs = logs?.items.filter((log) => {
    // Filter by level if selected
    if (selectedLevel && log.level !== selectedLevel) return false;
    // Filter by search query
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      log.message.toLowerCase().includes(query) ||
      log.source?.toLowerCase().includes(query)
    );
  });

  /**
   * Render loading state
   */
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h2 style={styles.title}>📜 Bot Logs</h2>
        </div>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}>🔄</div>
          <p style={styles.loadingText}>Loading logs...</p>
        </div>
      </div>
    );
  }

  /**
   * Render error state
   */
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h2 style={styles.title}>📜 Bot Logs</h2>
        </div>
        <div style={styles.errorContainer}>
          <span style={styles.errorIcon}>⚠️</span>
          <p style={styles.errorText}>Failed to load logs: {error}</p>
        </div>
      </div>
    );
  }

  /**
   * Render no data state
   */
  if (!logs || logs.items.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h2 style={styles.title}>📜 Bot Logs</h2>
        </div>
        <div style={styles.emptyContainer}>
          <p style={styles.emptyText}>
            {selectedLevel
              ? `No ${selectedLevel} logs found`
              : "No logs available"}
          </p>
        </div>
      </div>
    );
  }

  /**
   * Render logs viewer
   */
  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>📜 Bot Logs</h2>
          <p style={styles.subtitle}>
            Showing {filteredLogs?.length || 0} of {logs.total} logs
          </p>
        </div>
      </div>

      {/* Controls */}
      <div style={styles.controls}>
        {/* Search */}
        <div style={styles.searchContainer}>
          <span style={styles.searchIcon}>🔍</span>
          <input
            type="text"
            placeholder="Search logs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={styles.searchInput}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              style={styles.clearButton}
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>

        {/* Level Filter */}
        <div style={styles.filterContainer}>
          <label style={styles.filterLabel}>Level:</label>
          <select
            value={selectedLevel || ""}
            onChange={(e) =>
              setSelectedLevel(
                e.target.value ? (e.target.value as LogLevel) : undefined
              )
            }
            style={styles.filterSelect}
          >
            <option value="">All Levels</option>
            <option value={LogLevel.DEBUG}>Debug</option>
            <option value={LogLevel.INFO}>Info</option>
            <option value={LogLevel.WARNING}>Warning</option>
            <option value={LogLevel.ERROR}>Error</option>
            <option value={LogLevel.CRITICAL}>Critical</option>
          </select>
        </div>

        {/* Auto-scroll Toggle */}
        <div style={styles.toggleContainer}>
          <label style={styles.toggleLabel}>
            <input
              type="checkbox"
              checked={autoScroll}
              onChange={(e) => setAutoScroll(e.target.checked)}
              style={styles.toggleCheckbox}
            />
            <span style={styles.toggleText}>Auto-scroll</span>
          </label>
        </div>
      </div>

      {/* Logs List */}
      <div onScroll={handleLogsScroll} style={styles.logsContainer}>
        {filteredLogs && filteredLogs.length > 0 ? (
          <>
            {filteredLogs.map((log) => (
              <div key={log.id} style={styles.logEntry}>
                <div style={styles.logHeader}>
                  <span style={getLogLevelStyle(log.level)}>{log.level}</span>
                  <span style={styles.logTimestamp}>
                    {formatTimestamp(log.timestamp)}
                  </span>
                  {log.source && (
                    <span style={styles.logSource}>{log.source}</span>
                  )}
                </div>
                <div style={styles.logMessage}>{log.message}</div>
                {log.metadata && Object.keys(log.metadata).length > 0 && (
                  <details style={styles.logMetadata}>
                    <summary style={styles.metadataSummary}>
                      📋 Metadata
                    </summary>
                    <pre style={styles.metadataContent}>
                      {JSON.stringify(log.metadata, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            ))}
            <div ref={logsEndRef} />
          </>
        ) : (
          <div style={styles.emptyContainer}>
            <p style={styles.emptyText}>
              No logs match your search criteria
            </p>
          </div>
        )}
      </div>

      {/* Info Banner */}
      <div style={styles.infoBanner}>
        <span style={styles.infoIcon}>ℹ️</span>
        <span style={styles.infoText}>
          Logs are updated every 5 seconds. Recent logs appear at the bottom.
        </span>
      </div>
    </div>
  );
}

/**
 * Inline styles
 */
const styles: Record<string, React.CSSProperties> = {
  container: {
    width: "100%",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "1.5rem",
    paddingBottom: "1rem",
    borderBottom: `1px solid rgba(0,106,167,0.15)`,
  },
  title: {
    margin: 0,
    fontFamily: PIXEL,
    fontSize: "0.6rem",
    fontWeight: 400,
    color: YELLOW,
    letterSpacing: "0.08em",
    textShadow: `0 0 10px rgba(254,204,0,0.3)`,
  },
  subtitle: {
    margin: "0.5rem 0 0 0",
    fontFamily: MONO,
    fontSize: "0.8rem",
    color: "rgba(255,255,255,0.4)",
  },
  controls: {
    display: "flex",
    gap: "1rem",
    marginBottom: "1rem",
    flexWrap: "wrap",
    alignItems: "center",
  },
  searchContainer: {
    position: "relative",
    flex: 1,
    minWidth: "200px",
    display: "flex",
    alignItems: "center",
  },
  searchIcon: {
    position: "absolute",
    left: "0.75rem",
    fontSize: "1rem",
    pointerEvents: "none",
  },
  searchInput: {
    width: "100%",
    padding: "0.625rem 2.5rem 0.625rem 2.5rem",
    fontFamily: MONO,
    background: DARK_BG,
    border: `1px solid rgba(0,106,167,0.25)`,
    color: "rgba(255,255,255,0.87)",
    fontSize: "0.85rem",
    outline: "none",
    transition: "all 0.2s",
  },
  clearButton: {
    position: "absolute",
    right: "0.5rem",
    padding: "0.25rem 0.5rem",
    backgroundColor: "transparent",
    border: "none",
    color: "rgba(255,255,255,0.5)",
    fontSize: "1rem",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  filterContainer: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  filterLabel: {
    fontFamily: PIXEL,
    fontSize: "0.42rem",
    color: "rgba(255,255,255,0.5)",
    letterSpacing: "0.08em",
  },
  filterSelect: {
    padding: "0.625rem 0.75rem",
    fontFamily: MONO,
    background: DARK_BG,
    border: `1px solid rgba(0,106,167,0.25)`,
    color: "rgba(255,255,255,0.87)",
    fontSize: "0.85rem",
    cursor: "pointer",
    outline: "none",
    transition: "all 0.2s",
  },
  toggleContainer: {
    display: "flex",
    alignItems: "center",
  },
  toggleLabel: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    cursor: "pointer",
  },
  toggleCheckbox: {
    width: "1rem",
    height: "1rem",
    cursor: "pointer",
  },
  toggleText: {
    fontFamily: MONO,
    fontSize: "0.8rem",
    color: "rgba(255,255,255,0.5)",
  },
  logsContainer: {
    background: "rgba(0,10,18,0.95)",
    border: `1px solid rgba(0,106,167,0.12)`,
    padding: "1rem",
    maxHeight: "600px",
    overflowY: "auto",
    marginBottom: "1rem",
  },
  logEntry: {
    marginBottom: "1rem",
    paddingBottom: "1rem",
    borderBottom: `1px solid rgba(0,106,167,0.06)`,
  },
  logHeader: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    marginBottom: "0.5rem",
    flexWrap: "wrap",
  },
  logTimestamp: {
    fontFamily: MONO,
    fontSize: "0.75rem",
    color: "rgba(255,255,255,0.3)",
  },
  logSource: {
    fontFamily: MONO,
    fontSize: "0.75rem",
    color: "rgba(255,255,255,0.4)",
    background: "rgba(0,106,167,0.06)",
    padding: "0.25rem 0.5rem",
    border: `1px solid rgba(0,106,167,0.1)`,
  },
  logMessage: {
    fontFamily: MONO,
    fontSize: "0.85rem",
    color: "rgba(255,255,255,0.8)",
    lineHeight: 1.5,
    wordBreak: "break-word",
  },
  logMetadata: {
    marginTop: "0.5rem",
    cursor: "pointer",
  },
  metadataSummary: {
    fontFamily: MONO,
    fontSize: "0.75rem",
    color: "rgba(255,255,255,0.4)",
    cursor: "pointer",
    userSelect: "none",
  },
  metadataContent: {
    marginTop: "0.5rem",
    padding: "0.75rem",
    fontFamily: MONO,
    background: "rgba(0,106,167,0.03)",
    border: `1px solid rgba(0,106,167,0.08)`,
    fontSize: "0.75rem",
    color: "rgba(255,255,255,0.5)",
    overflowX: "auto",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "3rem",
    gap: "1rem",
  },
  spinner: {
    fontSize: "2rem",
    animation: "spin 1s linear infinite",
  },
  loadingText: {
    fontFamily: MONO,
    fontSize: "0.85rem",
    color: "rgba(255,255,255,0.5)",
    margin: 0,
  },
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "2rem",
    gap: "0.5rem",
    background: "rgba(239,68,68,0.08)",
    border: "1px solid rgba(239,68,68,0.25)",
  },
  errorIcon: {
    fontSize: "2rem",
  },
  errorText: {
    fontFamily: MONO,
    fontSize: "0.85rem",
    color: "#fca5a5",
    margin: 0,
    textAlign: "center",
  },
  emptyContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "3rem",
  },
  emptyText: {
    fontFamily: MONO,
    fontSize: "0.85rem",
    color: "rgba(255,255,255,0.3)",
    margin: 0,
  },
  infoBanner: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    background: `rgba(0,106,167,0.08)`,
    border: `1px solid rgba(0,106,167,0.25)`,
    padding: "1rem",
  },
  infoIcon: {
    fontSize: "1.25rem",
  },
  infoText: {
    flex: 1,
    fontFamily: MONO,
    fontSize: "0.8rem",
    color: BLUE,
  },
};
